type PlaybackEvent = 'DOMLoaded' | 'complete' | 'data_failed' | 'error' | 'destroy';

export interface InteractionAnimation {
  isLoaded: boolean;
  goToAndPlay(frame: number, isFrame: boolean): void;
  goToAndStop(frame: number, isFrame: boolean): void;
  pause(): void;
  addEventListener(event: PlaybackEvent, listener: () => void): () => void;
}

interface PlaybackSession {
  animation: InteractionAnimation;
  ready: boolean;
  onFailure: () => void;
}

/** One wait after loading or completion; old animation events cannot restart playback. */
export class InteractionPromptPlayback {
  private session?: PlaybackSession;
  private timer?: ReturnType<typeof setTimeout>;
  private listeners: (() => void)[] = [];
  private repeatDelay = 40_000;
  private destroyed = false;

  public attach(animation: InteractionAnimation, onFailure: () => void): void {
    if (this.destroyed) return;
    this.clear();
    const session: PlaybackSession = { animation, ready: false, onFailure };
    this.session = session;
    const ready = (): void => {
      if (this.session !== session || session.ready) return;
      session.ready = true;
      this.schedule(session);
    };

    this.listeners = [
      animation.addEventListener('DOMLoaded', ready),
      animation.addEventListener('complete', () => {
        if (this.session !== session || !session.ready) return;
        try {
          animation.goToAndStop(0, true);
          this.schedule(session);
        } catch {
          this.fail(session);
        }
      }),
      animation.addEventListener('data_failed', () => this.fail(session)),
      animation.addEventListener('error', () => this.fail(session)),
      animation.addEventListener('destroy', () => {
        // Lottie clears its listeners on destruction; do not remove them afterward.
        if (this.session === session) this.clear(true);
      }),
    ];
    if (animation.isLoaded) ready();
  }

  public setRepeatDelay(delay: number): void {
    if (this.repeatDelay === delay) return;
    this.repeatDelay = delay;
    if (this.timer !== undefined && this.session) this.schedule(this.session);
  }

  public clear(animationDestroyed = false): void {
    const previous = this.session;
    this.session = undefined;
    this.cancelTimer();
    if (!animationDestroyed) {
      for (const removeListener of this.listeners) removeListener();
      previous?.animation.pause();
    }
    this.listeners = [];
  }

  public destroy(): void {
    this.destroyed = true;
    this.clear();
  }

  private schedule(session: PlaybackSession): void {
    this.cancelTimer();
    this.timer = setTimeout(() => {
      this.timer = undefined;
      if (this.session !== session) return;
      try {
        session.animation.goToAndPlay(0, true);
      } catch {
        this.fail(session);
      }
    }, this.repeatDelay);
  }

  private fail(session: PlaybackSession): void {
    if (this.session !== session) return;
    this.clear();
    session.onFailure();
  }

  private cancelTimer(): void {
    if (this.timer !== undefined) clearTimeout(this.timer);
    this.timer = undefined;
  }
}
